import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import parse from 'html-react-parser';
import { PDFDocument } from 'pdf-lib';
import './FileDropZone.css';

import { IoClose } from "react-icons/io5";
import { IoMdCloudUpload } from "react-icons/io";

import PulseUpload from "../../assets/anims/PulseUpload/PulseUpload";
import Loader9Dots from '../../assets/anims/Loader9Dots/Loader9Dots';
import useNumCols from './numColumns';
import { sPushFile } from '../../utils/sPushFile';
import { dropzoneConf } from './dropzoneConf';

import SlIcon from '@shoelace-style/shoelace/dist/react/icon';
import SlSpinner from '@shoelace-style/shoelace/dist/react/spinner';



const formatNumPages = (n) => {
    if (n === 1) {
        return '1 pagina';
    } else {
        return `${n} pagine`;
    }
}


const FileDropZone = ({ uploadedFiles, setUploadedFiles, useCaseName, isWaiting }) => {

    const mimeType = dropzoneConf[useCaseName].mimeType;
    const fileExtension = dropzoneConf[useCaseName].fileExtension;
    const fileColor = dropzoneConf[useCaseName].fileColor;
    const allowMultipleFiles = dropzoneConf[useCaseName].allowMultipleFiles;
    const dropzoneHint = dropzoneConf[useCaseName].dropzoneHint;
    
    const [uploadInProgress, setUploadInProgress] = useState(false);


    const handleFileUpload = async (files) => {

        if (uploadInProgress) {
            return  // prevent making two uploads at the same time
        }
        
        if (!allowMultipleFiles && uploadedFiles.length > 0) {
            return  // prevent uploading multiple files when not allowed
        }

        setUploadInProgress(true);

        let newUploadedFiles = [...uploadedFiles];

        // add entry for each new file
        for (const file of files) {

            let numPages = null;
            if (mimeType === "application/pdf") {
                // load pdf file to get number of pages
                const arrayBuffer = await file.arrayBuffer();
                const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
                numPages = pdfDoc.getPageCount();
            }

            const newFileEntry = {
                fileName: file.name,
                numPages: numPages,
                s3Key: '',
                uploaded: false,
                deleting: false
            };
            newUploadedFiles = [...newUploadedFiles, newFileEntry];
        }
        setUploadedFiles([...newUploadedFiles]);  // always create a new array when updating a state
        console.log(newUploadedFiles);
        
        for (const file of files) {

            setUploadInProgress(true);

            setTimeout(() => {

                sPushFile(file, useCaseName)
                .then((uploadedKey) => {
                    console.log('Uploaded file key:', uploadedKey);
                    // update corresponding entry of uploadedFiles
                    newUploadedFiles = newUploadedFiles.map(entry => 
                        entry.fileName === file.name
                        ? { ...entry, s3Key: uploadedKey, uploaded: true }
                        : entry
                    );
                    setUploadedFiles([...newUploadedFiles]);  // always create a new array when updating a state
                    setUploadInProgress(false);
                })
                .catch((error) => {
                    console.error('Error during file upload:', error);
                    // remove the last entry to uploadedFiles
                    newUploadedFiles.pop();
                    setUploadedFiles([...newUploadedFiles]);  // always create a new array when updating a state
                    setUploadInProgress(false);
                });
                
            }, 300);  // simulating a brief delay
        }
    };


    const handleFileDelete = (index) => {
        const newUploadedFiles = [...uploadedFiles];
        newUploadedFiles[index].deleting = true;
        setUploadedFiles(newUploadedFiles);

        setTimeout(() => {
        setUploadedFiles(newUploadedFiles => newUploadedFiles.filter((_, i) => i !== index));
        }, 200);  // wait for animation to complete
    }


    const { getRootProps, getInputProps } = useDropzone({
        accept: {
            [mimeType]: [fileExtension]
        },
        onDrop: handleFileUpload,
        multiple: allowMultipleFiles // Allow multiple file selection
    });


    return (
        <div {...getRootProps()} className="drop-file-upload" >
            <input {...getInputProps()} />
            <div className="drop-file-upload-content" style={{ gridTemplateColumns: `repeat(${useNumCols(uploadedFiles, allowMultipleFiles)}, 1fr)` }} >
            {uploadedFiles.length > 0 ? (
                <>
                {uploadedFiles.map((file, index) => (
                    <div 
                        key={index} 
                        className={`file-card ${file.deleting ? 'deleting': ''}`}
                        onClick={(e) => e.stopPropagation()}  // stop the event propagation here
                    >
                    <div className="file-card-top-row">
                        <div className='file-tag' style={{ backgroundColor: fileColor }}>
                            {fileExtension.substring(1)}
                        </div>
                        {/* <img src={fileIcon} alt='File logo' height={28} width={28} /> */}
                        <button className='delete-btn' onClick={() => handleFileDelete(index)}>
                            <IoClose />
                        </button>
                    </div>
                    <span>{file.fileName}</span>
                    <div style={{ display: 'grid' }}>
                        {file.numPages && (
                            <span className='num-pages'>{formatNumPages(file.numPages)}</span>
                        )}
                        <div className={`upload-bar ${file.uploaded ? 'uploaded': ''}`} >
                            {file.uploaded ? (
                            <SlIcon name="check-lg" style={{ fontSize: "20px" }}></SlIcon>
                            ) : (
                            <SlSpinner className="upload-spinner" />
                            )}
                        </div>
                    </div>
                    </div>
                ))}
                {allowMultipleFiles && (
                    <div className="new-upload-card" >
                        <PulseUpload 
                            icon={<IoMdCloudUpload />}
                            size={"64px"}
                        />
                        {/* <SlIcon name="cloud-upload" style={{ fontSize: "24pt" }}></SlIcon> */}
                        <span>Clicca per caricare<br />altri files</span>
                    </div>
                )}
                </>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                    <PulseUpload 
                        icon={<IoMdCloudUpload />}
                        size={"64px"}
                    />
                    <span>{parse(dropzoneHint)}</span>
                </div>
            )}
            </div>

            {isWaiting && (
                <div 
                    className={"dropzone-darken-layer"}
                    onClick={(e) => {e.stopPropagation()}}  // disable dropzone
                >
                    <Loader9Dots style={{ height: "min-content" }} />
                </div>
            )}
            
        </div>
    );

};

export default FileDropZone;
